import { Timestamp } from 'firebase/firestore';
import { chain, concat, differenceBy, get, has, isEmpty, map, omit, reject } from 'lodash';
import ClinicSettingsUpdatesDB from 'src/fb/clinicSettingsUpdates-db';
import WorkflowsDB from 'src/fb/workflows-db';
import { auth as useAuthStore } from 'src/store/auth';

export default {
  setWorkflows(workflows) {
    this.workflows = workflows;
  }, // setWorkflows()

  setWorkflowsLastFetch(workflowIds) {
    const currentDate = new Date();
    this.workflowsLastFetchTimestamp = {
      ...(this.workflowsLastFetchTimestamp || {}),
      ...(workflowIds?.reduce?.((acc, workflowId) => {
        acc[workflowId] = currentDate;
        return acc;
      }, {}) || {}),
    };
  }, // setWorkflowsLastFetch()

  async createWorkflow(workflowObj) {
    const clinicId = get(workflowObj, 'clinic', null);
    await this.setWorkflowLastUpdate(clinicId);

    const workflowsDb = new WorkflowsDB();
    const newWorkflow = await workflowsDb.create(workflowObj);

    if (newWorkflow?.id) {
      this.setWorkflows([...this.workflows, newWorkflow]);
      this.setWorkflowsLastFetch([newWorkflow.id]);
    }

    return newWorkflow;
  }, // createWorkflow()

  async updateWorkflow(workflowObj) {
    const clinicId = get(workflowObj, 'clinic', null);
    await this.setWorkflowLastUpdate(clinicId);

    const workflowsDb = new WorkflowsDB();
    await workflowsDb.update(workflowObj);

    if (workflowObj?.id) {
      this.setWorkflows([...this.workflows, workflowObj]);
      this.setWorkflowsLastFetch([workflowObj.id]);
    }
  }, // updateWorkflow()

  /**
   * Workflows are only soft delete
   */
  async deleteWorkflow(workflow) {
    const clinicId = get(workflow, 'clinic', null);
    await this.setWorkflowLastUpdate(clinicId);

    const workflowsDb = new WorkflowsDB();
    await workflowsDb.set({ isDeleted: true }, workflow.id);
    this.setWorkflows(reject(this.workflows, { id: workflow.id }));
  }, // deleteWorkflow()

  async toggleWorkflowLock({ workflowId, isLocked }) {
    const workflowsDb = new WorkflowsDB();
    await workflowsDb.toggleWorkflowLock(workflowId, isLocked);
  }, // toggleWorkflowLock()

  async duplicateWorkflow(workflow) {
    const clinicId = get(workflow, 'clinic', null);
    await this.setWorkflowLastUpdate(clinicId);

    const workflowsDb = new WorkflowsDB();
    await workflowsDb.duplicateWorkflow(workflow);
  }, // duplicateWorkflow()

  async setWorkflowLastUpdate(clinicId) {
    if (clinicId !== null) {
      const clinicSettingsUpdatesDb = new ClinicSettingsUpdatesDB();
      await clinicSettingsUpdatesDb.set({ promptLevelTemplates: Timestamp.now() }, clinicId);
    }
  }, // setWorkflowLastUpdate()

  async reloadWorkflows(workflowIds) {
    if (isEmpty(workflowIds)) {
      return;
    }

    const auth = useAuthStore();

    const workflowSettingsLastUpdatedTs = get(auth, ['clinicSettingsUpdates', 'workflows'], null);
    const workflowSettingsLastUpdated = (workflowSettingsLastUpdatedTs instanceof Timestamp)
      ? workflowSettingsLastUpdatedTs.toDate()
      : null;

    const refreshWorkflowIds = chain(workflowIds)
      .map((workflowId) => {
        const workflowLastUpdated = get(this.workflowsLastFetchTimestamp, workflowId, null);
        if (workflowLastUpdated
          && (
            !workflowSettingsLastUpdated // Workflows never updated
            // Workflows updated after last load
          || (workflowLastUpdated > workflowSettingsLastUpdated)
          )
        ) {
          return null;
        }
        return workflowId;
      })
      .compact()
      .value();

    const workflowsDb = new WorkflowsDB();
    const loadedWorkflows = await workflowsDb.readMultiple(refreshWorkflowIds);

    if (isEmpty(this.workflows)) {
      this.setWorkflows(reject(loadedWorkflows, { isDeleted: true }));
    } else {
      // If loaded workflows already exist in the state, actualize the state by the loaded workflows
      // If not, add loaded workflows to the state
      const updatedWorkflows = concat(
        ...differenceBy(this.workflows, loadedWorkflows, 'id'),
        ...loadedWorkflows,
      );

      this.setWorkflows(reject(updatedWorkflows, { isDeleted: true }));
    }

    this.setWorkflowsLastFetch(map(loadedWorkflows, 'id'));
  }, // reloadWorkflows()

  async reloadAllWorkflows(clinic) {
    const workflowsDb = new WorkflowsDB();
    const workflows = await workflowsDb.readAll(
      [
        ['clinic', '==', clinic],
        ['isDeleted', '==', false],
      ],
      'workflowName',
    );

    this.setWorkflows(workflows);
    this.setWorkflowsLastFetch(map(workflows, 'id'));
  }, // reloadAllWorkflows()

  resetWorkflowCacheLastFetch(workflowId = null) {
    if (workflowId === null) {
      this.workflowsLastFetchTimestamp = {};
    } else {
      this.workflowsLastFetchTimestamp = omit(this.workflowsLastFetchTimestamp, workflowId);
    }
  }, // resetWorkflowCacheLastFetch()

  getWorkflowCustomStatuses(workflowId) {
    if (!workflowId) {
      return [];
    }

    return chain(this.workflows)
      .find({ id: workflowId })
      .get('statuses')
      .filter((status) => has(status, 'type'))
      .map('value')
      .value();
  }, // getWorkflowCustomStatuses()

  logout() {
    this.setWorkflows([]);
    this.resetWorkflowCacheLastFetch();
  }, // logout()

};
